import BaseLoader from './BaseLoader'


export default class PackFileLoader extends BaseLoader {

    constructor(scene) {
        super(scene)

        this.loadingPacks = []

        this.on('filecomplete', () => this.checkLoadingPacks())
        this.on('complete', () => this.checkLoadingPacks())
    }

    loadPack(key, url, callback, onError = null) {
        if (this.checkPackComplete(key)) {
            callback()
            return
        }

        const cleanListeners = () => {
            this.off(`packcomplete-${key}`, onPackComplete)

            if (onError) {
                this.off('loaderror', onPackError)
            }
        }

        this.once(`filecomplete-packfile-${key}`, key =>
            this.addLoadingPack(key)
        )

        const onPackComplete = () => {
            cleanListeners()
            this.removeLoadingPack(key)
            callback()
        }

        const onPackError = file => {
            if (!file || file.key !== key || file.type !== 'packfile') {
                return
            }

            cleanListeners()
            this.removeLoadingPack(key)

            if (typeof onError === 'function') {
                onError(file)
            }
        }

        this.once(`packcomplete-${key}`, onPackComplete)

        if (onError) {
            this.on('loaderror', onPackError)
        }

        this.pack(key, url)
        this.start()
    }

    addLoadingPack(key) {
        this.loadingPacks.push(key)
    }

    removeLoadingPack(key) {
        this.loadingPacks = this.loadingPacks.filter(packKey => packKey !== key)
    }

    checkLoadingPacks() {
        for (const key of this.loadingPacks) {
            if (this.checkPackComplete(key)) {
                this.removeLoadingPack(key)

                this.emit(`packcomplete-${key}`)
            }
        }
    }

    checkPackComplete(key) {
        if (!this.jsonExists(key)) {
            return false
        }

        const pack = this.scene.cache.json.get(key)

        for (const configKey in pack) {
            const files = pack[configKey]?.files

            if (Array.isArray(files) && !this.checkFilesComplete(files)) {
                return false
            }
        }

        return true
    }

    checkFilesComplete(files) {
        for (const file of files) {
            let exists = false

            switch (file.type) {
                case 'animation':
                    exists = this.animationsExists(file.key)
                    break

                case 'json':
                    exists = this.jsonExists(file.key)
                    break

                case 'audio':
                    exists = this.audioExists(file.key)
                    break

                case 'multiatlas':
                    exists = this.textureExists(file.key)
                    break

                case 'atlas':
                case 'atlasjson':
                case 'spritesheet':
                case 'image':
                case 'svg':
                case 'texture':
                    exists = this.textureExists(file.key)
                    break

                case 'bitmapFont':
                    exists = Boolean(this.scene.cache.bitmapFont?.exists(file.key))
                    break

                case 'html':
                case 'binary':
                case 'text':
                case 'xml':
                case 'glsl':
                case 'shader':
                case 'tilemapCSV':
                case 'tilemapTiledJSON':
                case 'unityAtlas':
                case 'video':
                case 'plugin':
                case 'script':
                    exists = true
                    break

                default:
                    exists = true
                    break
            }

            if (!exists) {
                return false
            }
        }

        return true
    }

    animationsExists(key) {
        if (!this.jsonExists(key)) {
            return false
        }

        const anims = this.scene.cache.json.get(key)?.anims

        if (!Array.isArray(anims)) {
            return false
        }

        for (const anim of anims) {
            if (!this.scene.anims.exists(anim.key)) {
                return false
            }
        }

        return true
    }

}
